import * as React from "react";
import {
  Box,
  Button,
  useMediaQuery,
  Drawer,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getApiDocsData } from "../../services/apiDocsService";
import { hasChildren } from "../../pages/dashboard/utils";
import { menu } from "./menu";
import DescriptionBox from "./DescriptionBox";
import APIServices from "./APIServices";
import CategoryOverview from "./CategoryOverview";
import { useLocation } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Loader } from "../loader";

const drawerWidth = 280;

export const APIDocument = () => {
  const location = useLocation();
  const [data, setData] = React.useState(null);
  const [showBackButton, setShowBackButton] = React.useState(null);
  const [selectedCategory, setSelectedCategory] = React.useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = React.useState(null);
  const [showCategoryOverview, setShowCategoryOverview] = React.useState(false);
  const [selectedTabValue, setSelectedTabValue] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [pdfLoading, setPdfLoading] = React.useState(false);
  const [expandedParentMenu, setExpandedParentMenu] = React.useState(null);

  const isMobile = useMediaQuery("(max-width:899px)");
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const tabRefs = React.useRef({});
  const leftSidebarRef = React.useRef(null);
  const rightContentRef = React.useRef(null);

  const scrollToTop = () => {
    if (isMobile) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (rightContentRef.current) {
      rightContentRef.current.scrollTo({ top: 0 });
    }
  };

  const handleCategoryClick = (value) => {
    scrollToTop();
    if (selectedCategory === value) {
      setSelectedCategory(null);
      setShowCategoryOverview(false);
      setSelectedTabValue(null);
    } else {
      setSelectedCategory(value);
      setShowCategoryOverview(true);
      setSelectedSubCategory(null);
      setSelectedTabValue(value);
    }
  };

  const handleApiClickFromOverview = (api) => {
    setSelectedSubCategory(api.title);
    setShowCategoryOverview(false);
    setTimeout(() => {
      scrollToPosition(apiServicesRef);
    }, 300);
  };

  React.useEffect(() => {
    const handleToggleDocMenu = () => setMobileMenuOpen((prev) => !prev);
    window.addEventListener("toggle-doc-menu", handleToggleDocMenu);
    return () => window.removeEventListener("toggle-doc-menu", handleToggleDocMenu);
  }, []);

  React.useEffect(() => {
    if (!isMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  const createApiDocumentPdf = () => {
    setPdfLoading(true);
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 10;
      const maxWidth = pageWidth - margin * 2;
      const greyColor = [30, 29, 28];
      const headerFill = [0, 112, 192];
      const ctx = { currentY: 20 };

      const addTitleWithLine = (text, x, y, fontSize = 16, color = headerFill) => {
        if (!text) return;
        doc.setFontSize(fontSize);
        doc.setTextColor(...color);
        doc.text(text, x, y);
        doc.setDrawColor(200);
        doc.line(x, y + 2, pageWidth - margin, y + 2);
        ctx.currentY = y + 12;
        doc.setTextColor(...greyColor);
      };

      const addParagraphText = (text, x, y, fontSize = 12) => {
        if (!text) return;
        doc.setFontSize(fontSize);
        doc.setTextColor(...greyColor);
        const lines = doc.splitTextToSize(String(text), maxWidth);
        let newY = y;
        if (newY + lines.length * 8 > pageHeight - margin) {
          doc.addPage();
          newY = margin;
          ctx.currentY = margin;
        }
        doc.text(lines, x, newY);
        ctx.currentY = newY + lines.length * 8 + 2;
      };

      const buildNameExplanationRows = (items = []) =>
        items.map((item) => [item?.name || '', item?.explanation || '']);

      const renderTable = (head, body) => {
        doc.autoTable({
          startY: ctx.currentY + 1,
          head: [head],
          body,
          margin: { left: margin },
          headStyles: {
            fillColor: headerFill,
            textColor: [255, 255, 255],
          },
        });
        ctx.currentY = doc.autoTable.previous.finalY + 10;
      };

      const renderApiNameBanner = (text) => {
        if (!text) return;
        const fontSize = 14;
        const backgroundHeight = fontSize * 0.6;
        const backgroundY = ctx.currentY - backgroundHeight + 2;

        doc.setFillColor(...headerFill);
        doc.rect(margin, backgroundY, pageWidth - margin * 2, backgroundHeight, 'F');
        doc.setFontSize(fontSize);
        doc.setTextColor(255, 255, 255);
        doc.text(text, margin + 2, ctx.currentY);

        ctx.currentY += backgroundHeight + 6;
        doc.setTextColor(...greyColor);
      };

      const renderApiCodeDataTable = (title, codeData) => {
        if (!codeData) return;
        addTitleWithLine(title, margin, ctx.currentY, 12);
        doc.autoTable({
          startY: ctx.currentY - 5,
          head: [['Name', 'Explanation']],
          body: buildNameExplanationRows(codeData),
          margin: { left: margin },
          headStyles: {
            fillColor: headerFill,
            textColor: [255, 255, 255],
          },
        });
        ctx.currentY = doc.autoTable.previous.finalY + 7;
      };

      const renderResponseSamples = (samples) => {
        if (!samples?.length) return;
        addTitleWithLine("Response Samples:", margin, ctx.currentY, 12);
        for (const sample of samples) {
          addParagraphText(`Code: ${sample.code || ''} - ${sample.description || ''}`, margin, ctx.currentY);
          addParagraphText(JSON.stringify(sample.json, null, 2), margin, ctx.currentY);
        }
      };

      const renderApiDetail = (apiDetail) => {
        renderApiNameBanner(apiDetail?.apiName);

        addTitleWithLine("Description", margin, ctx.currentY, 12);
        addParagraphText(`${apiDetail.APIDetail.description}`, margin, ctx.currentY);

        addTitleWithLine("Endpoint: ", margin, ctx.currentY, 12);
        addParagraphText(`${apiDetail.endpoint}`, margin, ctx.currentY);

        addTitleWithLine("Method: ", margin, ctx.currentY, 12);
        addParagraphText(`${apiDetail.apiMethod}`, margin, ctx.currentY);

        ctx.currentY += 2;

        renderApiCodeDataTable("Headers:", apiDetail.header?.codeData);
        renderApiCodeDataTable("Request Parameters:", apiDetail.requestParameter?.codeData);
        renderResponseSamples(apiDetail.responseSample);
      };

      const renderApiServices = (services = []) => {
        for (const service of services) {
          addParagraphText(service.categoryName, margin, ctx.currentY, 16);
          for (const apiDetail of service.apiDetails || []) {
            renderApiDetail(apiDetail);
          }
        }
      };

      addTitleWithLine(data?.Introduction.title, margin, ctx.currentY);
      addParagraphText(data.Introduction.description, margin, ctx.currentY + 1);

      addTitleWithLine(data.Authentication.title, margin, ctx.currentY + 2);
      addParagraphText(data.Authentication.description, margin, ctx.currentY + 1);

      addTitleWithLine(data?.httpStatusCode?.title, margin, ctx.currentY + 2);
      renderTable(
        ['Code', 'Name', 'Explanation'],
        data.httpStatusCode.codeData.map((item) => [
          item.code || '',
          item.name || '',
          item.explanation || '',
        ])
      );

      addTitleWithLine(data.rateLimit.title, margin, ctx.currentY + 2);
      addParagraphText(data.rateLimit.description, margin, ctx.currentY + 1);
      ctx.currentY += 10;

      addTitleWithLine('API Services', margin, ctx.currentY);
      renderApiServices(data?.apiServices);

      addTitleWithLine(data.glossary.title, margin, ctx.currentY + 10);
      doc.autoTable({
        startY: ctx.currentY + 2,
        head: [['Name', 'Explanation']],
        body: data.glossary.codeData.map((item) => [
          item.name || '',
          item.explanation || '',
        ]),
        margin: { left: margin },
        headStyles: {
          fillColor: headerFill,
          textColor: [255, 255, 255],
        },
      });

      doc.save('API_Documentation.pdf');
    } catch (err) {
      console.error("PDF generation failed", err);
    } finally {
      setPdfLoading(false);
    }
  };


  React.useEffect(() => {
    if (selectedSubCategory) {
      setShowCategoryOverview(false);
    }
  }, [selectedSubCategory]);

  const httpStatusCodeTableColumns = [
    { header: "Code", field: "code" },
    { header: "Name", field: "name" },
    { header: "Explanation", field: "explanation" },
  ];

  const httpStatusCodeTableRows = data?.httpStatusCode?.codeData?.map(
    (item) => ({
      code: item.code,
      name: item.name,
      explanation: item.explanation,
    })
  );

  const glossaryColumns = [
    { header: "Name", field: "name" },
    { header: "Explanation", field: "explanation" },
  ];

  const glossaryRows = data?.glossary?.codeData?.map((item) => ({
    name: item.name,
    explanation: item?.explanation,
  }));

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await getApiDocsData();
        setData(res?.data?.data?.attributes);

        if (location.state?.selectedCategory && location.state?.selectedSubCategory) {
          setSelectedCategory(location.state.selectedCategory);
          setSelectedSubCategory(location.state.selectedSubCategory);
          setShowBackButton(location.state.showBackButton);
          setSelectedTabValue(`${location.state.selectedCategory}-${location.state.selectedSubCategory}`);
          setTimeout(() => {
            scrollToPosition(apiServicesRef);
          }, 500);
        } else if (res?.data?.data?.attributes?.apiServices?.length > 0) {
          const firstCategory = res.data.data.attributes.apiServices[0];
          setSelectedCategory(firstCategory.categoryName);
          if (firstCategory.apiDetails.length > 0) {
            setSelectedSubCategory(firstCategory.apiDetails[0].APIDetail.title);
          }
        }
      } catch (error) {
        console.error("Error fetching API data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [location.state]);

  const apiServicesData = data?.apiServices?.map((service) => ({
    categoryName: service?.categoryName,
    categoryDescription: service?.description,
    commonUseCase: service?.commonUseCase,
    apiDetails: service?.apiDetails?.map((detail) => ({
      category: service?.categoryName,
      title: detail?.APIDetail?.title,
      description: detail?.APIDetail?.description,
      endpoint: detail?.endpoint,
      method: detail?.apiMethod,
      headerTitle: detail?.header?.title,
      headerDescription: detail?.header?.description,
      headerValue: detail?.header?.codeData?.map((headerItem) => ({
        name: headerItem?.name,
        explanation: headerItem?.explanation,
      })),
      requestParameterTitle: detail?.requestParameter?.title,
      requestParameterDescription: detail?.requestParameter?.description,
      requestParameterValue: detail?.requestParameter?.codeData?.map(
        (paramItem) => ({
          name: paramItem?.name,
          explanation: paramItem?.explanation,
        })
      ),
      curlCommand: detail?.curlCommand,
      requestSample: detail?.requestSample,
      responseSample: detail?.responseSample?.map((responseItem) => ({
        code: responseItem?.code,
        description: responseItem?.description,
        json: responseItem?.json,
      })),
    })),
  }));

  const introductionRef = React.useRef(null);
  const authenticationRef = React.useRef(null);
  const httpStatusCodeRef = React.useRef(null);
  const rateLimitRef = React.useRef(null);
  const apiServicesRef = React.useRef(null);
  const apiDocumentIdentification = React.useRef(null);
  const glossaryRef = React.useRef(null);


  const scrollToPosition = (targetRef) => {
    if (!targetRef?.current) return;
    if (isMobile) {
      const rect = targetRef.current.getBoundingClientRect();
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      window.scrollTo({
        top: scrollTop + rect.top - 70,
        behavior: "smooth",
      });
    } else {
      if (!rightContentRef.current) return;
      const container = rightContentRef.current;
      const targetOffset = targetRef.current.offsetTop;
      container.scrollTo({
        top: targetOffset - 125,
        behavior: "smooth",
      });
    }
  };

  React.useEffect(() => {
    if (!selectedTabValue) return;
  
    const sidebar = leftSidebarRef.current;
    const tabEl = tabRefs.current[selectedTabValue];
  
    if (sidebar && tabEl) {
      const topPosition = tabEl.offsetTop - 40; 
      sidebar.scrollTo({ top: topPosition, behavior: "smooth" });
    }
  }, [selectedTabValue]);
  
  const handleDrawerItemClick = (item) => {
    scrollToTop();

    setSelectedTabValue(item.title);
    if (!dropdownOptions.some(opt => opt.value === item.title)) {
      console.log("Clicked on item:", item.title);
    }
    if (item.title !== "Fintech API Services" && !dropdownOptions.some(opt => opt.value === item.title)) {
      setShowCategoryOverview(false);
      setSelectedCategory(null);
      setSelectedSubCategory(null);
    }
    switch (item.title) {
      case "Introduction":
        scrollToPosition(introductionRef);
        break;
      case "Authentication":
        scrollToPosition(authenticationRef);
        break;
      case "HTTP Status Codes":
        scrollToPosition(httpStatusCodeRef);
        break;
      case "Rate Limit":
        scrollToPosition(rateLimitRef);
        break;
      case "Fintech API Services":
        scrollToPosition(apiServicesRef);
        break;
      case "Document Identification":
        scrollToPosition(apiDocumentIdentification);
        break;
      case "Glossary":
        scrollToPosition(glossaryRef);
        break;
      default:
        break;
    }
  };

  const dropdownOptions =
    data?.apiServices?.map((item) => ({
      label: item.categoryName,
      value: item.categoryName,
      subCategories: item.apiDetails.map((detail) => detail.APIDetail.title),
    })) || [];

  const HEADER_HEIGHT = 80;

  const setTabRef = (key) => (el) => {
    tabRefs.current[key] = el;
  };

  const handleSubCategoryClick = (e, opt, sub, subValue) => {
    e.stopPropagation();
    scrollToTop();
    setSelectedSubCategory(sub);
    setSelectedTabValue(subValue);
    setMobileMenuOpen(false);
    setTimeout(() => {
      scrollToPosition(apiServicesRef);
    }, 100);
  };

  const handleDropdownOptionClick = (e, optValue) => {
    e.stopPropagation();
    handleCategoryClick(optValue);
  };

  const handleParentMenuClick = (e, itemTitle) => {
    e.stopPropagation();
    setExpandedParentMenu(
      expandedParentMenu === itemTitle ? null : itemTitle
    );
  };

  const handleLeafMenuClick = (e, itemTitle) => {
    e.stopPropagation();
    handleDrawerItemClick({ title: itemTitle });
    setMobileMenuOpen(false);
  };

  const renderSubCategoryItem = (opt, sub) => {
    const subValue = `${opt.value}-${sub}`;
    return (
      <Box
        ref={setTabRef(subValue)}
        key={subValue}
        onClick={(e) => handleSubCategoryClick(e, opt, sub, subValue)}
        sx={{
          pl: 5,
          pr: 2,
          minHeight: "48px",
          display: "flex",
          alignItems: "center",
          fontSize: "13px",
          color: "#020817",
          cursor: "pointer",
          backgroundColor:
            selectedTabValue === subValue ? "#DCE2F8" : "transparent",
          borderLeft:
            selectedTabValue === subValue
              ? "4px solid #4B6FFF"
              : "4px solid transparent",
          "&:hover": {
            backgroundColor: "#F4F5F7",
          },
        }}
      >
        {sub}
      </Box>
    );
  };

  const renderDropdownOption = (opt) => (
    <React.Fragment key={opt.value}>
      <Box
        ref={setTabRef(opt.value)}
        onClick={(e) => handleDropdownOptionClick(e, opt.value)}
        sx={{
          pl: 3,
          pr: 2,
          minHeight: "48px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: "13px",
          color: "#020817",
          cursor: "pointer",
          backgroundColor:
            selectedTabValue === opt.value ? "#DCE2F8" : "transparent",
          borderLeft:
            selectedTabValue === opt.value
              ? "4px solid #4B6FFF"
              : "4px solid transparent",
          "&:hover": {
            backgroundColor: "#F4F5F7",
          },
        }}
      >
        {opt.label}
        {selectedCategory === opt.value ? (
          <KeyboardArrowDownIcon fontSize="small" />
        ) : (
          <KeyboardArrowRightIcon fontSize="small" />
        )}
      </Box>
      {selectedCategory === opt.value &&
        opt.subCategories.map((sub) => renderSubCategoryItem(opt, sub))}
    </React.Fragment>
  );

  const renderParentMenuItem = (item) => (
    <React.Fragment key={item.title}>
      <Box
        ref={setTabRef(item.title)}
        onClick={(e) => handleParentMenuClick(e, item.title)}
        sx={{
          px: 2,
          minHeight: "48px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: "16px",
          color: "#020817",
          fontWeight: 500,
          cursor: "pointer",
          backgroundColor:
            expandedParentMenu === item.title ? "#DCE2F8" : "transparent",
          borderLeft:
            expandedParentMenu === item.title
              ? "4px solid #4B6FFF"
              : "4px solid transparent",
          "&:hover": {
            backgroundColor: "#F4F5F7",
          },
        }}
      >
        {item.title}
        {expandedParentMenu === item.title ? (
          <KeyboardArrowDownIcon fontSize="small" />
        ) : (
          <KeyboardArrowRightIcon fontSize="small" />
        )}
      </Box>
      {expandedParentMenu === item.title &&
        dropdownOptions.map((opt) => renderDropdownOption(opt))}
    </React.Fragment>
  );

  const renderLeafMenuItem = (item) => (
    <Box
      key={item.title}
      ref={setTabRef(item.title)}
      onClick={(e) => handleLeafMenuClick(e, item.title)}
      sx={{
        px: 2,
        minHeight: "48px",
        display: "flex",
        alignItems: "center",
        fontSize: "16px",
        color: "#020817",
        cursor: "pointer",
        backgroundColor:
          selectedTabValue === item.title ? "#DCE2F8" : "transparent",
        borderLeft:
          selectedTabValue === item.title
            ? "4px solid #4B6FFF"
            : "4px solid transparent",
        "&:hover": {
          backgroundColor: "#F4F5F7",
        },
      }}
    >
      {item.title}
    </Box>
  );

  const renderSidebar = () => (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      {menu.map((item) =>
        hasChildren(item) ? renderParentMenuItem(item) : renderLeafMenuItem(item)
      )}
    </Box>
  );

  return (
    <>
    {(loading || pdfLoading) && <Loader />}
    <Box
      sx={{
        display: "flex",
        background: "#ffffff",
        flexDirection: "row",
        width: "100%",
        minHeight: "100vh",
      }}
    >


      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          anchor="left"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          sx={{
            "& .MuiDrawer-paper": {
              width: "280px",
              paddingTop: "10px",
            },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1 }}>
            <IconButton onClick={() => setMobileMenuOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box ref={leftSidebarRef} sx={{ overflowY: "auto", flex: 1 }}>
            {renderSidebar()}
          </Box>
        </Drawer>
      )}

      {/* Desktop Sidebar */}
      {!isMobile && (
        <Box
          ref={leftSidebarRef}
          sx={{
            width: { sm: "250px", md: "280px" },
            minWidth: { sm: "220px", md: "280px" },
            borderRight: 1,
            borderColor: "divider",
            paddingTop: { md: "10px", lg: "70px" },
            backgroundColor: "#fff",
            height: "100vh",
            overflowY: "auto",
            position: "sticky",
            top: 0,
          }}
        >
          {renderSidebar()}
        </Box>
      )}

      <Box
        ref={rightContentRef}
        sx={{
          flex: 1,
          minWidth: 0,
          height: { xs: "auto", md: `calc(100vh - ${HEADER_HEIGHT}px)` },
          paddingTop: { xs: "60px", md: `${HEADER_HEIGHT}px` },
          paddingLeft: { xs: 1, sm: 2 },
          paddingRight: { xs: 1, sm: 2 },
          paddingBottom: 2,
          overflowY: { xs: "visible", md: "auto" },
          overscrollBehavior: "contain",
        }}
      >
        <Box
          sx={{ display: "flex", justifyContent: "end", paddingRight: "15px",
            "@media (max-width: 767px)": {
              marginTop:"20px",
            },
           }}
        >
          <Button onClick={createApiDocumentPdf} variant="contained">
            Download PDF
          </Button>
        </Box>

        <div id="api-document-content">
          {showCategoryOverview ? (
            <CategoryOverview
              categoryData={data?.apiServices?.find(
                (service) => service.categoryName === selectedCategory
              )}
              onApiClick={handleApiClickFromOverview}
            />
          ) : (
            <>
              <DescriptionBox
                title={data?.Introduction?.title}
                refProp={introductionRef}
                description={data?.Introduction?.description}
              />
              <DescriptionBox
                title={data?.Authentication?.title}
                refProp={authenticationRef}
                description={data?.Authentication?.description}
              />
              <DescriptionBox
                title={data?.httpStatusCode?.title}
                refProp={httpStatusCodeRef}
                description={data?.httpStatusCode?.description}
                showBasicTable
                columns={httpStatusCodeTableColumns}
                rows={httpStatusCodeTableRows}
              />
              <DescriptionBox
                title={data?.rateLimit?.title}
                refProp={rateLimitRef}
                description={data?.rateLimit?.description}
              />
              <APIServices
                refProp={apiServicesRef}
                apiServicesData={apiServicesData}
                selectedCategory={selectedCategory}
                selectedSubCategory={selectedSubCategory}
                showBackButton={showBackButton}
                openAll={false}
              />
              <DescriptionBox
                title={data?.glossary?.title}
                refProp={glossaryRef}
                description={data?.glossary?.description}
                showBasicTable
                columns={glossaryColumns}
                rows={glossaryRows}
              />
            </>
          )}
        </div>
      </Box>
    </Box>
    </>
  );
};
